import { createAsyncThunk } from '@reduxjs/toolkit';
import { postGroupSacResync, getGroupDevicesCommandTargets, postGroupDevicesCommand } from 'services/api';
import {
  getGroup,
  postGroup,
  getMeasuresGroupRules,
  postMeasuresGroupRules,
  deleteMeasuresGroupRules,
  putMeasuresGroupRules,
  getGroupMessage,
  putGroup,
  postGroupServiceNumbers,
  putGroupServiceNumbers,
  postGroupNotification,
  putGroupNotification,
  getGroupNotifications,
  putGroupNotificationSend,
  deleteGroupNotification,
  getServiceReasonType,
  postServiceStateReason,
  putServiceStateReason,
  deleteServiceStateReason,
  restoreServiceStateReason,
  getResolutions,
  postResolution,
  putResolution,
  getGroupServiceTypesAdmin,
  postGroupServiceType,
  putGroupServiceType,
  archiveGroupServiceType,
  restoreGroupServiceType,
  getGroupServiceReviewConfig,
  getGroupServiceReviewConfigResolved,
  putGroupServiceReviewConfig,
  deleteGroupServiceReviewConfig,
  getGroupServiceStateAlertConfig,
  postGroupServiceStateAlertConfig,
  putGroupServiceStateAlertConfig,
  deleteGroupServiceStateAlertConfig,
  getNotMeasuredConfig,
  postNotMeasuredConfig,
  putNotMeasuredConfig,
  getNotMeasuredDeviceTypes,
  putNotMeasuredDeviceType,
  deleteNotMeasuredDeviceType,
  getServiceTypesForGroup,
  getNotMeasuredServiceRules,
  postNotMeasuredServiceRule,
  putNotMeasuredServiceRule,
  deleteNotMeasuredServiceRule,
} from 'services/api';
// Note: getServiceStateReasonsAsync also re-hydrates the masters
// `serviceTypeReason` slice via an extraReducer in mastersSlice (see comment
// there), so we don't need a separate dispatch after each mutation.
import {
  getGroupRequiredFields,
  getGroupRequiredFieldsResolved,
  getGroupRequiredFieldsCatalog,
  putGroupRequiredFields,
  deleteGroupRequiredFields,
} from 'services/api';
import {
  getGroupFhirConfig,
  putGroupFhirConfig,
  deleteGroupFhirConfig,
  getGroupMeasureSyncConfig,
  putGroupMeasureSyncConfig,
  deleteGroupMeasureSyncConfig,
} from 'services/api';
import { selectOptionsMeasuresGenericTypes, selectOptionsMeasuresUnits } from 'features/masters/mastersSelectors';

const buildRefreshRulesPayload = (state: any, groupId: any) => ({
  groupId,
  optionMeasureUnits: selectOptionsMeasuresUnits(state),
  optionGenericMeasureTypes: selectOptionsMeasuresGenericTypes(state),
});

export const getGroupAsync: any | null = createAsyncThunk('group/getGroup', async (data: any) => {
  const response = await getGroup(data);
  return response?.data;
});

export const postGroupAsync: any | null = createAsyncThunk('group/postGroup', async (data: any) => {
  const response = await postGroup(data);
  return response;
});

export const putGroupAsync: any | null = createAsyncThunk('group/putGroup', async (data: any) => {
  const response = await putGroup(data);
  return response;
});

export const postGroupServiceNumbersAsync: any | null = createAsyncThunk(
  'group/postGroupServiceNumbers',
  async (data: any) => {
    const response = await postGroupServiceNumbers(data);
    return response;
  },
);

export const putGroupServiceNumbersAsync: any | null = createAsyncThunk(
  'group/putGroupServiceNumbers',
  async (data: any) => {
    const response = await putGroupServiceNumbers(data);
    return response;
  },
);

export const getGroupMessageAsync: any | null = createAsyncThunk('group/getGroupMessage', async () => {
  const response = await getGroupMessage();
  return response?.data;
});

export const getMeasuresGroupRulesAsync: any | null = createAsyncThunk(
  'group/getMeasuresGroupRules',
  async (data: any) => {
    const response = await getMeasuresGroupRules(data.groupId);
    return response;
  },
);

export const postMeasuresGroupRulesAsync: any | null = createAsyncThunk(
  'group/postMeasuresGroupRules',
  async (data: any, { dispatch, getState }) => {
    const response = await postMeasuresGroupRules(data);
    dispatch(getMeasuresGroupRulesAsync(buildRefreshRulesPayload(getState(), data.groupId)));
    return response;
  },
);

export const putMeasuresGroupRulesAsync: any | null = createAsyncThunk(
  'group/putMeasuresGroupRules',
  async (data: any, { dispatch, getState }) => {
    const response = await putMeasuresGroupRules(data);
    dispatch(getMeasuresGroupRulesAsync(buildRefreshRulesPayload(getState(), data.groupId)));
    return response;
  },
);

export const deleteMeasuresGroupRulesAsync: any | null = createAsyncThunk(
  'group/deleteMeasuresGroupRules',
  async (data: any) => {
    const response = await deleteMeasuresGroupRules(data.id);
    return response;
  },
);

export const getGroupNotificationsAsync: any | null = createAsyncThunk(
  'userDetail/getGroupNotifications',
  async (data: any) => {
    const response = await getGroupNotifications(data);
    return response;
  },
);

export const postGroupNotificationAsync: any | null = createAsyncThunk(
  'userDetail/postGroupNotification',
  async (data: any, { dispatch }) => {
    const response = await postGroupNotification(data);
    if (response?.data.success) {
      dispatch(
        getGroupNotificationsAsync({
          id: data.groupId,
          init: 0,
          size: 10,
        }),
      );
    }
    return response;
  },
);

export const putGroupNotificationAsync: any | null = createAsyncThunk(
  'userDetail/putGroupNotification',
  async (data: any, { dispatch }) => {
    const response = await putGroupNotification(data);
    if (response?.data.success) {
      dispatch(
        getGroupNotificationsAsync({
          id: data.groupId,
          init: 0,
          size: 10,
        }),
      );
    }
    return response;
  },
);

export const putGroupNotificationSendAsync: any | null = createAsyncThunk(
  'userDetail/putGroupNotificationSend',
  async (data: any, { dispatch }) => {
    const response = await putGroupNotificationSend(data);
    if (response?.data.success) {
      dispatch(
        getGroupNotificationsAsync({
          id: data.groupId,
          init: 0,
          size: 10,
        }),
      );
    }
    return response;
  },
);

/**
 * Reenvía a SAC los pacientes del grupo y sus descendientes. El backend responde en cuanto ha
 * encolado el trabajo, así que el número devuelto son pacientes encolados, no aplicados.
 *
 * Sin estado en el slice a propósito (como putGroupNotificationSendAsync): es una acción puntual
 * cuyo resultado solo interesa en la pantalla que la lanza. Los errores los saca solos el
 * errorNotificationMiddleware al rechazarse el thunk.
 */
export const postGroupSacResyncAsync: any | null = createAsyncThunk(
  'group/postGroupSacResync',
  async (groupId: any) => {
    const response = await postGroupSacResync(groupId);
    return response?.data;
  },
);

/**
 * Comandos remotos a nivel de grupo. Igual que el resync a SAC: sin estado en el slice, porque el
 * resultado solo interesa en la pantalla que lanza la acción.
 *
 * `targets` previsualiza el alcance (no publica nada) y `command` encola el envío: lo que devuelve
 * son dispositivos encolados, no confirmaciones del dispositivo.
 */
export const getGroupDevicesCommandTargetsAsync: any | null = createAsyncThunk(
  'group/getGroupDevicesCommandTargets',
  async (data: any) => {
    const response = await getGroupDevicesCommandTargets(data.groupId, {
      includeDescendants: data.includeDescendants,
      deviceTypes: data.deviceTypes,
    });
    return response?.data;
  },
);

export const postGroupDevicesCommandAsync: any | null = createAsyncThunk(
  'group/postGroupDevicesCommand',
  async (data: any) => {
    const response = await postGroupDevicesCommand(data.groupId, {
      command: data.command,
      includeDescendants: data.includeDescendants,
      deviceTypes: data.deviceTypes,
    });
    return response?.data;
  },
);

export const delgroupNotificationAsync: any | null = createAsyncThunk(
  'userDetail/deleteGroupNotification',
  async (data: any, { dispatch }) => {
    const response = await deleteGroupNotification(data);
    if (response?.data.success) {
      dispatch(
        getGroupNotificationsAsync({
          id: data.groupId,
          init: 0,
          size: 10,
        }),
      );
    }
    return response;
  },
);

export const getServiceStateReasonsAsync: any | null = createAsyncThunk('group/getServiceStateReasons', async () => {
  const response = await getServiceReasonType();
  return response?.data;
});

export const postServiceStateReasonAsync: any | null = createAsyncThunk(
  'group/postServiceStateReason',
  async (data: any, { dispatch }) => {
    const response = await postServiceStateReason(data);
    if (response?.data?.success) {
      dispatch(getServiceStateReasonsAsync());
    }
    return response;
  },
);

export const putServiceStateReasonAsync: any | null = createAsyncThunk(
  'group/putServiceStateReason',
  async (data: any, { dispatch }) => {
    const response = await putServiceStateReason(data);
    if (response?.data?.success) {
      dispatch(getServiceStateReasonsAsync());
    }
    return response;
  },
);

export const archiveServiceStateReasonAsync: any | null = createAsyncThunk(
  'group/archiveServiceStateReason',
  async (id: string, { dispatch }) => {
    const response = await deleteServiceStateReason(id);
    if (response?.data?.success) {
      dispatch(getServiceStateReasonsAsync());
    }
    return response;
  },
);

export const restoreServiceStateReasonAsync: any | null = createAsyncThunk(
  'group/restoreServiceStateReason',
  async (id: string, { dispatch }) => {
    const response = await restoreServiceStateReason(id);
    if (response?.data?.success) {
      dispatch(getServiceStateReasonsAsync());
    }
    return response;
  },
);

// Resolutions (RESOLUTIONS catalog). Mirrors the service-state-reason CRUD but
// as a flat list (resolutions are not grouped by service state) and with an
// extra `stopsAccumulationCount` flag. Archive/restore reuse the PUT endpoint
// (the backend persists `archived`), so there is no dedicated restore route.
// getGroupResolutionsAsync also re-hydrates the masters `resolutions` slice via
// an extraReducer in mastersSlice, keeping the alarm-resolution picker in sync.
export const getGroupResolutionsAsync: any | null = createAsyncThunk('group/getResolutions', async () => {
  const response = await getResolutions();
  return response?.data;
});

export const postResolutionAsync: any | null = createAsyncThunk(
  'group/postResolution',
  async (data: any, { dispatch }) => {
    const response = await postResolution(data);
    if (response?.data?.success) {
      dispatch(getGroupResolutionsAsync());
    }
    return response;
  },
);

export const putResolutionAsync: any | null = createAsyncThunk(
  'group/putResolution',
  async (data: any, { dispatch }) => {
    const response = await putResolution(data);
    if (response?.data?.success) {
      dispatch(getGroupResolutionsAsync());
    }
    return response;
  },
);

export const getGroupServiceStateAlertConfigAsync: any | null = createAsyncThunk(
  'group/getGroupServiceStateAlertConfig',
  async (groupId: string) => {
    const response = await getGroupServiceStateAlertConfig(groupId);
    return response?.data;
  },
);

export const postGroupServiceStateAlertConfigAsync: any | null = createAsyncThunk(
  'group/postGroupServiceStateAlertConfig',
  async (data: { groupId: string; serviceStateId: string; enabled: boolean; priority: number }, { dispatch }) => {
    const { groupId, ...body } = data;
    const response = await postGroupServiceStateAlertConfig(groupId, body);
    if (response?.data?.success) {
      dispatch(getGroupServiceStateAlertConfigAsync(groupId));
    }
    return response;
  },
);

export const putGroupServiceStateAlertConfigAsync: any | null = createAsyncThunk(
  'group/putGroupServiceStateAlertConfig',
  async (
    data: { id: string; groupId: string; serviceStateId: string; enabled: boolean; priority: number },
    { dispatch },
  ) => {
    const { id, groupId, ...body } = data;
    const response = await putGroupServiceStateAlertConfig(groupId, id, { id, groupId, ...body });
    if (response?.data?.success) {
      dispatch(getGroupServiceStateAlertConfigAsync(groupId));
    }
    return response;
  },
);

export const deleteGroupServiceStateAlertConfigAsync: any | null = createAsyncThunk(
  'group/deleteGroupServiceStateAlertConfig',
  async (data: { id: string; groupId: string }, { dispatch }) => {
    const response = await deleteGroupServiceStateAlertConfig(data.groupId, data.id);
    if (response?.data?.success) {
      dispatch(getGroupServiceStateAlertConfigAsync(data.groupId));
    }
    return response;
  },
);

// --- Not-measured supervisor: config por grupo ---

export const getNotMeasuredConfigAsync: any | null = createAsyncThunk(
  'group/getNotMeasuredConfig',
  async (groupId: string) => {
    const response = await getNotMeasuredConfig(groupId);
    return response?.data;
  },
);

export const saveNotMeasuredConfigAsync: any | null = createAsyncThunk(
  'group/saveNotMeasuredConfig',
  async (data: { groupId: string; id?: string | null } & Record<string, any>, { dispatch }) => {
    const { groupId, id, ...body } = data;
    const response = id
      ? await putNotMeasuredConfig(groupId, id, { id, groupId, ...body })
      : await postNotMeasuredConfig(groupId, body);
    if (response?.data?.success) {
      dispatch(getNotMeasuredConfigAsync(groupId));
    }
    return response;
  },
);

// --- Not-measured supervisor: relevancia de dispositivos por grupo ---

export const getNotMeasuredDeviceTypesAsync: any | null = createAsyncThunk(
  'group/getNotMeasuredDeviceTypes',
  async (groupId: string) => {
    const response = await getNotMeasuredDeviceTypes(groupId);
    return response?.data;
  },
);

export const setNotMeasuredDeviceTypeOverrideAsync: any | null = createAsyncThunk(
  'group/setNotMeasuredDeviceTypeOverride',
  async (data: { groupId: string; deviceTypeId: string; evaluate: boolean }, { dispatch }) => {
    const response = await putNotMeasuredDeviceType(data.groupId, data.deviceTypeId, {
      evaluate: data.evaluate,
    });
    if (response?.data?.success) {
      dispatch(getNotMeasuredDeviceTypesAsync(data.groupId));
    }
    return response;
  },
);

export const clearNotMeasuredDeviceTypeOverrideAsync: any | null = createAsyncThunk(
  'group/clearNotMeasuredDeviceTypeOverride',
  async (data: { groupId: string; deviceTypeId: string }, { dispatch }) => {
    const response = await deleteNotMeasuredDeviceType(data.groupId, data.deviceTypeId);
    if (response?.data?.success) {
      dispatch(getNotMeasuredDeviceTypesAsync(data.groupId));
    }
    return response;
  },
);

// --- Not-measured supervisor: reglas (umbral) por tipo de servicio ---

export const getServiceTypesForGroupAsync: any | null = createAsyncThunk(
  'group/getServiceTypesForGroup',
  async (groupId: string) => {
    const response = await getServiceTypesForGroup(groupId);
    return response?.data;
  },
);

// Administración de tipos de servicio del grupo (incluye archivados, para poder reactivarlos). Vive en un
// slot de estado propio para no pisar `groupServiceTypes`, que solo trae los activos y alimenta el
// desplegable de reglas de falta de medida.
export const getGroupServiceTypesAdminAsync: any | null = createAsyncThunk(
  'group/getServiceTypesAdmin',
  async (groupId: string) => {
    const response = await getGroupServiceTypesAdmin(groupId);
    return response?.data;
  },
);

export const postGroupServiceTypeAsync: any | null = createAsyncThunk(
  'group/postServiceType',
  async ({ groupId, data }: any, { dispatch }) => {
    const response = await postGroupServiceType(groupId, data);
    if (response?.data?.success) {
      dispatch(getGroupServiceTypesAdminAsync(groupId));
    }
    return response;
  },
);

export const putGroupServiceTypeAsync: any | null = createAsyncThunk(
  'group/putServiceType',
  async ({ groupId, id, data }: any, { dispatch }) => {
    const response = await putGroupServiceType(groupId, id, data);
    if (response?.data?.success) {
      dispatch(getGroupServiceTypesAdminAsync(groupId));
    }
    return response;
  },
);

export const archiveGroupServiceTypeAsync: any | null = createAsyncThunk(
  'group/archiveServiceType',
  async ({ groupId, id }: any, { dispatch }) => {
    const response = await archiveGroupServiceType(groupId, id);
    if (response?.data?.success) {
      dispatch(getGroupServiceTypesAdminAsync(groupId));
    }
    return response;
  },
);

export const restoreGroupServiceTypeAsync: any | null = createAsyncThunk(
  'group/restoreServiceType',
  async ({ groupId, id }: any, { dispatch }) => {
    const response = await restoreGroupServiceType(groupId, id);
    if (response?.data?.success) {
      dispatch(getGroupServiceTypesAdminAsync(groupId));
    }
    return response;
  },
);

export const getNotMeasuredServiceRulesAsync: any | null = createAsyncThunk(
  'group/getNotMeasuredServiceRules',
  async (groupId: string) => {
    const response = await getNotMeasuredServiceRules(groupId);
    return response?.data;
  },
);

export const postNotMeasuredServiceRuleAsync: any | null = createAsyncThunk(
  'group/postNotMeasuredServiceRule',
  async (data: { groupId: string } & Record<string, any>, { dispatch }) => {
    const { groupId, ...body } = data;
    const response = await postNotMeasuredServiceRule(groupId, body);
    if (response?.data?.success) {
      dispatch(getNotMeasuredServiceRulesAsync(groupId));
    }
    return response;
  },
);

export const putNotMeasuredServiceRuleAsync: any | null = createAsyncThunk(
  'group/putNotMeasuredServiceRule',
  async (data: { groupId: string; id: string } & Record<string, any>, { dispatch }) => {
    const { groupId, id, ...body } = data;
    const response = await putNotMeasuredServiceRule(groupId, id, { id, groupId, ...body });
    if (response?.data?.success) {
      dispatch(getNotMeasuredServiceRulesAsync(groupId));
    }
    return response;
  },
);

export const deleteNotMeasuredServiceRuleAsync: any | null = createAsyncThunk(
  'group/deleteNotMeasuredServiceRule',
  async (data: { groupId: string; id: string }, { dispatch }) => {
    const response = await deleteNotMeasuredServiceRule(data.groupId, data.id);
    if (response?.data?.success) {
      dispatch(getNotMeasuredServiceRulesAsync(data.groupId));
    }
    return response;
  },
);

// --- Required-fields validation config (campos mínimos para evolucionar el estado) ---

// Carga la configuración propia del grupo (own: {enabled,fields} | null), el valor
// efectivo (resolved, con herencia) y el catálogo de campos seleccionables.
export const getGroupRequiredFieldsConfigAsync: any | null = createAsyncThunk(
  'group/getGroupRequiredFieldsConfig',
  async (groupId: string, { getState }) => {
    const locale = (getState() as any)?.settings?.locale;
    const [ownR, resolvedR, catalogR] = await Promise.allSettled([
      getGroupRequiredFields(groupId),
      getGroupRequiredFieldsResolved(groupId),
      getGroupRequiredFieldsCatalog(groupId, locale),
    ]);
    // own y resolved son imprescindibles: si fallan, la pestaña muestra error.
    if (ownR.status === 'rejected') throw ownR.reason;
    if (resolvedR.status === 'rejected') throw resolvedR.reason;
    const resolvedItem = resolvedR.value?.data?.item ?? { enabled: false, fields: [] };
    // El catálogo solo alimenta los checkboxes: si falla, se degrada a vacío (no tumba la pestaña).
    const catalog = catalogR.status === 'fulfilled' ? catalogR.value?.data?.items ?? [] : [];
    return {
      own: ownR.value?.data?.item ?? null, // null = el grupo hereda (sin configuración propia)
      resolved: { enabled: resolvedItem.enabled === true, fields: resolvedItem.fields ?? [] },
      catalog, // [{ code, label }]
    };
  },
);

export const putGroupRequiredFieldsConfigAsync: any | null = createAsyncThunk(
  'group/putGroupRequiredFieldsConfig',
  async (data: { groupId: string; enabled: boolean; fields: string[] }, { dispatch }) => {
    const response = await putGroupRequiredFields(data.groupId, { enabled: data.enabled, fields: data.fields });
    if (response?.data?.success) {
      dispatch(getGroupRequiredFieldsConfigAsync(data.groupId));
    }
    return response;
  },
);

export const deleteGroupRequiredFieldsConfigAsync: any | null = createAsyncThunk(
  'group/deleteGroupRequiredFieldsConfig',
  async (groupId: string, { dispatch }) => {
    const response = await deleteGroupRequiredFields(groupId);
    if (response?.data?.success) {
      dispatch(getGroupRequiredFieldsConfigAsync(groupId));
    }
    return response;
  },
);

// --- Configuración de la alerta de revisión del servicio ---

// Carga la fila propia (own: {enabled,notifyLeadDays,priority} | null) y el valor efectivo con herencia.
// Las dos son imprescindibles para la pestaña, así que un fallo en cualquiera la deja en error.
export const getGroupServiceReviewConfigAsync: any | null = createAsyncThunk(
  'group/getServiceReviewConfig',
  async (groupId: string) => {
    const [ownR, resolvedR] = await Promise.all([
      getGroupServiceReviewConfig(groupId),
      getGroupServiceReviewConfigResolved(groupId),
    ]);
    const resolvedItem = resolvedR?.data?.item ?? {};
    return {
      groupId,
      own: ownR?.data?.item ?? null, // null = el grupo hereda
      resolved: {
        enabled: resolvedItem.enabled === true,
        notifyLeadDays: resolvedItem.notifyLeadDays ?? 0,
        priority: resolvedItem.priority ?? 2,
        sourceGroupId: resolvedItem.sourceGroupId,
        sourceGroupName: resolvedItem.sourceGroupName,
      },
    };
  },
);

export const putGroupServiceReviewConfigAsync: any | null = createAsyncThunk(
  'group/putServiceReviewConfig',
  async (data: { groupId: string; enabled: boolean; notifyLeadDays: number; priority: number }, { dispatch }) => {
    const response = await putGroupServiceReviewConfig(data.groupId, {
      enabled: data.enabled,
      notifyLeadDays: data.notifyLeadDays,
      priority: data.priority,
    });
    if (response?.data?.success) {
      dispatch(getGroupServiceReviewConfigAsync(data.groupId));
    }
    return response;
  },
);

export const deleteGroupServiceReviewConfigAsync: any | null = createAsyncThunk(
  'group/deleteServiceReviewConfig',
  async (groupId: string, { dispatch }) => {
    const response = await deleteGroupServiceReviewConfig(groupId);
    if (response?.data?.success) {
      dispatch(getGroupServiceReviewConfigAsync(groupId));
    }
    return response;
  },
);

// --- Integración FHIR/TSV (pestaña de Integración de grupo, solo superadmin) ---

// Carga en una pasada la config FHIR efectiva (fhir: null = sin config en toda la cadena) y la
// tabla de canales por tipo de medida (sync.items, con own/sourceGroupId para propia vs heredada).
export const getGroupIntegrationConfigAsync: any | null = createAsyncThunk(
  'group/getGroupIntegrationConfig',
  async (groupId: string) => {
    const [fhirR, syncR] = await Promise.allSettled([getGroupFhirConfig(groupId), getGroupMeasureSyncConfig(groupId)]);
    if (fhirR.status === 'rejected') throw fhirR.reason;
    if (syncR.status === 'rejected') throw syncR.reason;
    return {
      fhir: fhirR.value?.data?.item ?? null,
      sync: syncR.value?.data?.item ?? { items: [], own: false, sourceGroupId: null },
    };
  },
);

export const putGroupFhirConfigAsync: any | null = createAsyncThunk(
  'group/putGroupFhirConfig',
  async (data: { groupId: string; config: any }, { dispatch }) => {
    const response = await putGroupFhirConfig(data.groupId, data.config);
    if (response?.data?.success) {
      // await: el thunk no resuelve hasta que el store tiene la config recargada — así el
      // componente limpia su dirty contra el estado NUEVO y no hay ventana de revert visual
      await dispatch(getGroupIntegrationConfigAsync(data.groupId));
    }
    return response;
  },
);

export const deleteGroupFhirConfigAsync: any | null = createAsyncThunk(
  'group/deleteGroupFhirConfig',
  async (groupId: string, { dispatch }) => {
    const response = await deleteGroupFhirConfig(groupId);
    if (response?.data?.success) {
      await dispatch(getGroupIntegrationConfigAsync(groupId));
    }
    return response;
  },
);

export const putGroupMeasureSyncConfigAsync: any | null = createAsyncThunk(
  'group/putGroupMeasureSyncConfig',
  async (data: { groupId: string; items: any[] }, { dispatch }) => {
    const response = await putGroupMeasureSyncConfig(data.groupId, { items: data.items });
    if (response?.data?.success) {
      await dispatch(getGroupIntegrationConfigAsync(data.groupId));
    }
    return response;
  },
);

export const deleteGroupMeasureSyncConfigAsync: any | null = createAsyncThunk(
  'group/deleteGroupMeasureSyncConfig',
  async (groupId: string, { dispatch }) => {
    const response = await deleteGroupMeasureSyncConfig(groupId);
    if (response?.data?.success) {
      await dispatch(getGroupIntegrationConfigAsync(groupId));
    }
    return response;
  },
);
